import React from 'react';
import TRAVEL_PROJECTS from '../data/travelProjects';

// Traveler line = the places he has actually lived.
const PATH_LINES = {
  engineer: 'NARA → NADA → UTOKYO ECON → CYBOZU → BCIT CS → FREELANCE ENGINEER',
  traveler: 'IKARUGA / NARA → KOBE → TOKYO → VANCOUVER / CANADA → …',
};

function resolveImagePath(imagePath) {
  const normalizedPath = imagePath.replace(/^\/+/, '');
  return `${process.env.PUBLIC_URL}/${normalizedPath}`;
}

function HeroBackdrop() {
  return (
    <div className="hero__backdrop" aria-hidden="true">
      <svg className="hero__grid" viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false">
        <defs>
          <pattern id="heroGrid" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M8 0H0V8" fill="none" stroke="currentColor" strokeWidth="0.12" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#heroGrid)" />
      </svg>
      <svg className="hero__route" viewBox="0 0 720 320" preserveAspectRatio="xMidYMid slice" focusable="false">
        <path
          className="hero__route-path"
          d="M-20 268 C 90 240, 130 150, 236 168 S 420 260, 500 176 S 640 60, 760 88"
          fill="none"
        />
        <circle className="hero__route-port" cx="236" cy="168" r="3.4" />
        <circle className="hero__route-port" cx="500" cy="176" r="3.4" />
      </svg>
    </div>
  );
}

// statement: array of segments; segments with `m: true` get the auto-highlight
// sweep (like someone selecting the text for you) after the page loads.
function Hero({ language, mode, projectCount }) {
  const isTraveler = mode === 'traveler';

  const copy =
    language === 'ja'
      ? {
          kicker: isTraveler ? 'SOFTWARE ENGINEER & TRAVELER' : 'SOFTWARE ENGINEER — PORTFOLIO',
          nameMain: '𠮷田 起弥',
          nameSub: 'TATSUYA YOSHIDA',
          statement: isTraveler
            ? [
                { t: 'コードを書くのも、知らない土地を走るのも、根っこは同じ。' },
                { t: '自分の足で確かめながら', m: true },
                { t: '、プロダクトと世界を前に進めます。' },
              ]
            : [
                { t: '商談の言葉からデプロイ後の運用まで。' },
                { t: 'ビジネスとコード、両方の言葉', m: true },
                { t: 'でプロダクトをつくるフリーランスエンジニアです。' },
              ],
          stats: isTraveler
            ? [
                { value: '34カ国', label: '訪問した国' },
                { value: String(TRAVEL_PROJECTS.length), label: '旅のプロジェクト' },
                { value: String(projectCount), label: '公開プロジェクト' },
              ]
            : [
                { value: String(projectCount), label: '公開プロジェクト' },
                { value: '約150社', label: '業務改善・導入支援' },
                { value: 'FE→Ops', label: 'フロントから運用まで' },
              ],
          status: '新規のご相談を受付中',
          location: 'Nara / Tokyo, JP',
          ctaPrimary: isTraveler ? '旅の記録を見る' : '作品を見る',
          ctaSecondary: '相談する',
          ariaLabel: 'イントロダクション',
          portraitAlt: isTraveler ? 'トゥクトゥクと本人の写真' : 'プロフィール写真',
        }
      : {
          kicker: isTraveler ? 'SOFTWARE ENGINEER & TRAVELER' : 'SOFTWARE ENGINEER — PORTFOLIO',
          nameMain: 'TATSUYA YOSHIDA',
          nameSub: '𠮷田 起弥',
          statement: isTraveler
            ? [
                { t: 'Writing code and crossing unfamiliar country come from the same place: ' },
                { t: 'verify with your own feet', m: true },
                { t: ', then move the product — and the map — forward.' },
              ]
            : [
                { t: 'I build web products end-to-end — ' },
                { t: 'from the sales conversation to the deployed code', m: true },
                { t: ', and the operations after that.' },
              ],
          stats: isTraveler
            ? [
                { value: '34', label: 'Countries visited' },
                { value: String(TRAVEL_PROJECTS.length), label: 'Journeys logged' },
                { value: String(projectCount), label: 'Works shipped' },
              ]
            : [
                { value: String(projectCount), label: 'Works shipped' },
                { value: '~150', label: 'Companies advised' },
                { value: 'FE→Ops', label: 'Frontend to operations' },
              ],
          status: 'Open to new projects',
          location: 'Nara / Tokyo, JP',
          ctaPrimary: isTraveler ? 'See the journeys' : 'View selected work',
          ctaSecondary: 'Get in touch',
          ariaLabel: 'Introduction',
          portraitAlt: isTraveler ? 'With the tuk-tuk' : 'Portrait photo',
        };

  const portrait = resolveImagePath(
    isTraveler ? 'images/profile_pictures/uni_tuktuk.jpg' : 'images/profile_pictures/cybozu.jpg'
  );

  let markIndex = -1;

  return (
    <section id="top" className="hero" aria-label={copy.ariaLabel}>
      <HeroBackdrop />

      <div className="hero__inner">
        <div className="hero__id">
          <img className="hero__portrait" src={portrait} alt={copy.portraitAlt} />
          <p className="hero__kicker mono">{copy.kicker}</p>
        </div>

        <h1 className="hero__name">
          <span className="hero__name-main">{copy.nameMain}</span>
          <span className="hero__name-sub mono">{copy.nameSub}</span>
        </h1>

        <p className="hero__statement">
          {copy.statement.map((segment, index) => {
            if (!segment.m) {
              return <span key={index}>{segment.t}</span>;
            }
            markIndex += 1;
            return (
              <em key={index} className="hero-mark" style={{ '--mark-i': markIndex }}>
                {segment.t}
              </em>
            );
          })}
        </p>

        <p className="hero__path mono">{PATH_LINES[isTraveler ? 'traveler' : 'engineer']}</p>

        <dl className="hero__stats">
          {copy.stats.map((stat) => (
            <div key={stat.label} className="hero__stat">
              <dt className="hero__stat-label">{stat.label}</dt>
              <dd className="hero__stat-value">{stat.value}</dd>
            </div>
          ))}
        </dl>

        <div className="hero__foot">
          <p className="hero__meta mono">
            <span className="hero__status-dot" aria-hidden="true" />
            {copy.status} · {copy.location}
          </p>

          <div className="hero__cta">
            <a href={isTraveler ? '#travels' : '#works'} className="button button--primary">
              {copy.ctaPrimary}
            </a>
            <a href="#contact" className="button button--ghost">
              {copy.ctaSecondary}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
